import React, { useRef } from 'react';
import { graphql, Link, StaticQuery } from 'gatsby';
import { buildImageObj, cn, emptyValues, sortByTitle } from '../../lib/helpers';
import BlockContent from '../block-content';
import * as styles from './student-profile.module.css';
import * as layoutStyles from '../layout/layout.module.css';
import { generateSocialLinks, ConditionalWrapper } from '../../utils/tools';
import { GatsbyImage } from 'gatsby-plugin-image';
import Lightbox from '../lightbox';
import PortfolioGrid from '../grids/portfolio-grid';
import VideoPlayer from '../video-player';
import ContactUs from '../sections/contact-us';
import ContactForm from '../forms/contact-form';
import StudentNav from './student-nav';
import { imageUrlFor } from '../../lib/image-url';
import Section from '../sections/section';
import IndustryGrid from '../sections/industry-grid';

function StudentProfile(props) {
  // console.log('student profile props: ', props);
  const {
    id: studentId,
    _id,
    _rawBio,
    _rawDescription,
    name,
    degree,
    major,
    isHideMajor,
    embedCode,
    layout,
    school,
    awards,
    instructors,
    heroImage,
    portfolio,
    resume,
    externalSiteUrls,
    collabProjects,
    additionalPortfolioUrls,
  } = props;

  const layoutOneCol ='1col'; // this is the 1 column option value used in the Student schema's `layout` field

  const lightbox = useRef();
  const isArchitecture = school?.title?.trim().toLowerCase() === 'architecture';

  let extraVideos = [];

  if (additionalPortfolioUrls && additionalPortfolioUrls.length) {
    extraVideos = additionalPortfolioUrls.map((url, i) => ({
      _type: 'video',
      _key: `extraVideo${i}`,
      url,
    }));
  }

  const media = [...portfolio, ...extraVideos];

  // - don't display any hero images flagged as a head shot on the Student Profile page
  // - use case: the hero headshot feature is primarily used for Acting and Music students, where the headshot is only displayed
  //    in the students grid of School page.   Any additional hero images that are NOT flagged as a headshot are then displayed
  //    in the Student Profile page
  const filteredHeroImages = heroImage ? heroImage.filter( hero => !hero.isHeadShot) : [];

  // console.log(`*** SVU: student-profile: heroImage, filteredHeroImages, filteredHeroImages.length: `, {heroImage, filteredHeroImages}, filteredHeroImages.length)

  let collabHeroes = [];
  if (isArchitecture && collabProjects && collabProjects.length > 0) {
    collabHeroes = sortByTitle(collabProjects).map((project) => project.heroImage && project.heroImage[0]);
  }

  return (
    <StaticQuery
      query={graphql`
        {
          facebookColorIcon: file(relativePath: { eq: "social/facebook@2x.png" }) {
            childImageSharp {
              gatsbyImageData(width: 46, placeholder: NONE, layout: FIXED)
            }
          }
          instagramColorIcon: file(relativePath: { eq: "social/instagram@2x.png" }) {
            childImageSharp {
              gatsbyImageData(width: 46, placeholder: NONE, layout: FIXED)
            }
          }
          twitterColorIcon: file(relativePath: { eq: "social/twitter@2x.png" }) {
            childImageSharp {
              gatsbyImageData(width: 46, placeholder: NONE, layout: FIXED)
            }
          }
          linkedinColorIcon: file(relativePath: { eq: "social/linkedin@2x.png" }) {
            childImageSharp {
              gatsbyImageData(width: 46, placeholder: NONE, layout: FIXED)
            }
          }
          pinterestColorIcon: file(relativePath: { eq: "social/pinterest@2x.png" }) {
            childImageSharp {
              gatsbyImageData(width: 46, placeholder: NONE, layout: FIXED)
            }
          }
          behanceColorIcon: file(relativePath: { eq: "social/behance@2x.png" }) {
            childImageSharp {
              gatsbyImageData(width: 46, placeholder: NONE, layout: FIXED)
            }
          }
          snapchatColorIcon: file(relativePath: { eq: "social/snapchat@2x.png" }) {
            childImageSharp {
              gatsbyImageData(width: 46, placeholder: NONE, layout: FIXED)
            }
          }
          whatsappColorIcon: file(relativePath: { eq: "social/whatsapp@2x.png" }) {
            childImageSharp {
              gatsbyImageData(width: 46, placeholder: NONE, layout: FIXED)
            }
          }
          youtubeColorIcon: file(relativePath: { eq: "social/youtube@2x.png" }) {
            childImageSharp {
              gatsbyImageData(width: 46, placeholder: NONE, layout: FIXED)
            }
          }
        }
      `}
      render={({
        behanceIconColor,
        facebookColorIcon,
        instagramColorIcon,
        linkedinColorIcon,
        twitterColorIcon,
        pinterestColorIcon,
        snapchatColorIcon,
        whatsappColorIcon,
        youtubeColorIcon,
      }) => {
        return (
          <div>
            {/* styles.columnWrapperOneColumnLayout is based on layoutStyles.columnWrapper except anything that causes 1 column layout issue, e.g., flex */}
            <article className={layout == layoutOneCol ? styles.columnWrapperOneColumnLayout : layoutStyles.columnWrapper}>

              {/*
                * Notes on click handler of items in the assets column.  The following is the order in which the assets appear
                * and what happens on click:
                *   1.  `featuredHeroImages` (Hero Images not marked as headshot)
                *     - onClick: opens lightbox carousel with the clicked hero image active
                *   2.  `collabHeroes` (Collaborative Projects); for Architecture students only
                *     - onClick: send user to the clicked Collaborative Project page
                *       - however, the Collaborative Projects will still appear in the Lightbox carousel if the lightbox carousel is activated by clicking on either #1 (hero) or #2 (portfolio) item
                *         - see the `media` property of the <Lightbox> component below
                *   3.  `portolio` items and `additionalPortforlioUrls`, which are combined into the `media` array
                *     - onClick: opens lightbox carousel with the clicked portfolio item active
                */}
              <div className={layoutStyles.leftColumn}>
                {filteredHeroImages?.length > 0 &&
                  filteredHeroImages.map((hero, idx) => {
                    // console.log("buildImageObj(hero.image): ", buildImageObj(hero.image))
                    // console.log("imageUrlFor(buildImageObj(hero.image)): ", imageUrlFor(buildImageObj(hero.image)))
                    return (
                      <button
                        type="button"
                        key={idx}
                        className={cn(layoutStyles.mediaInteractWrapper, layoutStyles.mediaInteractWrapperHero)}
                        // the argument to the openItem() call is the order/position that the item occurs in the <Lightbox> component's `media` property (see below)
                        onClick={() => lightbox.current.openItem(idx)}
                      >
                        {hero.image && hero.image.asset && (
                          <>
                            <GatsbyImage
                              image={hero?.image?.asset?.gatsbyImageData}
                              className={styles.mainImage}
                              alt={hero.alt || 'hero image'}
                              imgStyle={{ objectFit: 'contain' }}
                            />
                            <div className={layoutStyles.preloadHidden}>
                              <img
                                src={imageUrlFor(buildImageObj(hero.image)).url()}
                                width="1"
                                height="1"
                                alt="Hidden preloader"
                              />
                            </div>
                            {hero.caption && hero.caption.trim() !== '' && (
                              <div className={layoutStyles.caption}>{hero.caption}</div>
                            )}
                          </>
                        )}
                        {hero._type === 'video' && hero.url && <VideoPlayer url={hero.url} thumbnail />}
                      </button>
                    );
                  })}

                {isArchitecture && collabProjects && collabProjects.length > 0 && (
                  <div>
                    <h4 className={styles.sectionTitle}>Student Projects</h4>

                    {sortByTitle(collabProjects).map(
                      (project) => {
                        // console.log("project: ", project);
                        // console.log("buildImageObj(project.heroImage[0]): ", buildImageObj(project.heroImage[0].image));

                        // NOTE: clicking on a collabProject in the left column will not activate the Lightbox carousel like clicking on 
                        //        a hero image, portfolio, or additional portfolio item.   However, the collab project will appear in the
                        //        the lightbox carousel if it is activated by clicking on one of the aforementioned items.  See <Lightbox> component below
                        return project.heroImage &&
                          project.heroImage[0] &&
                          project.heroImage[0].image && (
                            <div key={project.heroImage[0]._key} className={styles.mainImage}>
                              <ConditionalWrapper
                                condition={project?.school && project?.school?.slug && project?.slug}
                                wrapper={(children) => (
                                  <Link
                                    className={styles.collabProjectsLink}
                                    to={`/schools/${project?.school?.slug.current}/projects/${project?.slug?.current}`}
                                  >
                                    {children}
                                  </Link>
                                )}
                              >
                                {project.heroImage[0] && (
                                  <>
                                    <GatsbyImage
                                      image={project.heroImage[0]?.image?.asset?.gatsbyImageData}
                                      alt={project.heroImage[0].alt}
                                      imgStyle={{ objectFit: 'contain' }}
                                    />
                                    <div className={layoutStyles.preloadHidden}>
                                      <img
                                        src={imageUrlFor(buildImageObj(project.heroImage[0]?.image)).url()}
                                        width="1"
                                        height="1"
                                        alt="Hidden preloader"
                                      />
                                    </div>
                                  </>
                                )}
                              </ConditionalWrapper>
                            </div>
                          )
                      }
                    )}
                  </div>
                )}

                {isArchitecture && media.length > 0 && <h4 className={styles.sectionTitle}>Student work</h4>}

                <PortfolioGrid
                  media={media} // includes Portfolio items and Extra 
                  showDescription={isArchitecture}
                  // Set the appropriate Lightbox carousel index for each medias (ie portfolio and additionalPortfolioUrl item) 
                  // since the <Lightbox> compoent below contains all three arrays
                  //
                  // the argument to the openItem() call is the order/position that the item occurs in the <Lightbox> component's `media` property (see below)
                  onItemClick={(idx) => lightbox.current.openItem(idx + filteredHeroImages?.length + collabHeroes?.length)}
                />

                <Lightbox ref={lightbox} media={[...filteredHeroImages, ...collabHeroes, ...media]} />

                {embedCode && <div className={styles.embedCode} dangerouslySetInnerHTML={{ __html: embedCode }} />}
              </div>
              {/* styles.rightColumnOneColumnLayout is based on layoutStyles.rightColumn except anything that causes 1 column layout issue */}
              <div className={ layout == layoutOneCol ? styles.rightColumnOneColumnLayout : layoutStyles.rightColumn}>
                <div className={layoutStyles.breadcrumb}>
                  <Link to={'/'}>HOME</Link>
                  <span className={layoutStyles.breadcrumbLinkSeparator}>&gt;</span>
                  <Link to={`/schools/${school?.slug?.current}`}>{school?.title}</Link>
                  <span className={layoutStyles.breadcrumbLinkSeparator}>&gt;</span>
                  <div className={layoutStyles.breadcrumbLink}>{name}</div>
                </div>
                <h1 className={layoutStyles.title}>{name}</h1>
                <div className={layoutStyles.subTitle}>
                  {!isArchitecture && school ? `${school.title} /` : null} {degree ? degree.code : null}
                </div>
                {isHideMajor != 'true' && major?.title ? <div className={layoutStyles.major}>{major.title}</div> : null }
                {_rawBio && (
                  <div className={layoutStyles.bio}>
                    <BlockContent blocks={_rawBio || []} />
                  </div>
                )}

                {awards && awards.length > 0 && !emptyValues(awards) && (
                  <div className={layoutStyles.columnSection}>
                    <div className={layoutStyles.subTitle}>Awards</div>
                    {awards.map((award, idx) => (
                      <div key={idx}>{award}</div>
                    ))}
                  </div>
                )}

                <ContactForm studentName={name} studentId={_id} />

                {externalSiteUrls && (
                  <div className={cn(styles.externalUrls, layoutStyles.columnSection)}>
                    {externalSiteUrls &&
                      generateSocialLinks(externalSiteUrls, {
                        behance: behanceIconColor,
                        facebook: facebookColorIcon,
                        instagram: instagramColorIcon,
                        linkedin: linkedinColorIcon,
                        pinterest: pinterestColorIcon,
                        snapchat: snapchatColorIcon,
                        twitter: twitterColorIcon,
                        whatsapp: whatsappColorIcon,
                        youtube: youtubeColorIcon,
                      })}
                  </div>
                )}

                {resume && resume.asset && (
                  <div className={layoutStyles.columnSection}>
                    <a
                      style={{ textDecoration: 'underline' }}
                      target="_blank"
                      rel="noopener"
                      href={`${resume.asset.url}`}
                    >
                      Download R&eacute;sum&eacute;
                    </a>
                  </div>
                )}

                {isArchitecture && instructors && instructors.length > 0 && !emptyValues(instructors) && (
                  <div className={layoutStyles.columnSection}>
                    <div className={layoutStyles.subTitle}>Faculty</div>
                    {instructors.map((instructor, idx) => (
                      <div key={idx}>{instructor}</div>
                    ))}
                  </div>
                )}

                {_rawDescription && (
                  <div className={layoutStyles.columnSection}>
                    <BlockContent blocks={_rawDescription || []} />
                  </div>
                )}

                <StudentNav schoolSlug={school?.slug?.current} studentId={studentId} />

                {!isArchitecture && collabProjects && collabProjects.length > 0 && (
                  <div className={layoutStyles.columnSection}>
                    <h4>Collaborative Projects</h4>

                    {sortByTitle(collabProjects).map(
                      (project) => {
                       return project.heroImage &&
                         project.heroImage[0] &&
                         project.heroImage[0].image && (
                           <div key={project.heroImage[0]._key} className={styles.mainImage}>
                             <ConditionalWrapper
                               condition={project?.school && project?.school?.slug && project?.slug}
                               wrapper={(children) => (
                                 <Link
                                   className={styles.collabProjectsLink}
                                   to={`/schools/${project?.school?.slug?.current}/projects/${project?.slug?.current}`}
                                 >
                                   {children}
                                 </Link>
                               )}
                             >
                               <GatsbyImage
                                 image={project.heroImage[0].image.asset?.gatsbyImageData}
                                 alt={project.heroImage[0].alt}
                                 imgStyle={{ objectFit: 'contain' }}
                               />
                               <div className={layoutStyles.preloadHidden}>
                                 <img
                                   src={imageUrlFor(buildImageObj(project.heroImage[0]?.image)).url()}
                                   width="1"
                                   height="1"
                                   alt="Hidden preload"
                                 />
                               </div>
                             </ConditionalWrapper>
                           </div>
                         )
                      }
                    )}
                  </div>
                )}
              </div>
            </article>
            <Section>
              <IndustryGrid hiringCompanies={school?.hiringCompanies} />
            </Section>
            <ContactUs />
          </div>
        );
      }}
    />
  );
}

export default StudentProfile;
